import { useState } from "react";
import {
  User,
  MapPin,
  Package,
  Lightbulb,
  BookOpen,
  StickyNote,
  Plus,
  X,
  Search,
  Trash2,
  Link2,
  GripVertical,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppStore, type BubbleType } from "@/lib/store";

interface BubblePanelProps {
  className?: string;
}

const bubbleTypes: {
  type: BubbleType;
  label: string;
  icon: typeof User;
  color: string;
}[] = [
    {
      type: "character",
      label: "Character",
      icon: User,
      color: "text-rose-500 bg-rose-50",
    },
    {
      type: "location",
      label: "Location",
      icon: MapPin,
      color: "text-emerald-500 bg-emerald-50",
    },
    {
      type: "item",
      label: "Item",
      icon: Package,
      color: "text-amber-500 bg-amber-50",
    },
    {
      type: "plot",
      label: "Plot",
      icon: Lightbulb,
      color: "text-violet-500 bg-violet-50",
    },
    {
      type: "research",
      label: "Research",
      icon: BookOpen,
      color: "text-blue-500 bg-blue-50",
    },
    {
      type: "note",
      label: "Note",
      icon: StickyNote,
      color: "text-slate-500 bg-slate-50",
    },
  ];

export function BubblePanel({ className }: BubblePanelProps) {
  const {
    activeNovelId,
    novels,
    createBubble,
    deleteBubble,
    setSelectedBubble,
    setDraggedBubble,
    themeProfile,
  } = useAppStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newBubbleType, setNewBubbleType] = useState<BubbleType>("character");
  const [newBubbleName, setNewBubbleName] = useState("");
  const [newBubbleContent, setNewBubbleContent] = useState("");
  const [filterType, setFilterType] = useState<BubbleType | "all">("all");

  const [activeTab, setActiveTab] = useState<"bubbles" | "notes">("bubbles");
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  const activeNovel = novels.find((n) => n.id === activeNovelId);
  const bubbles = activeNovel?.bubbles || [];
  const stickyNotes = activeNovel?.stickyNotes || [];
  const stickyLinks = activeNovel?.stickyLinks || [];

  const filteredStickies = stickyNotes.filter(note =>
    note.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateBubble = () => {
    if (newBubbleName.trim()) {
      createBubble(
        newBubbleType,
        newBubbleName.trim(),
        newBubbleContent.trim(),
      );
      setNewBubbleName("");
      setNewBubbleContent("");
      setShowCreateForm(false);
    }
  };

  const filteredBubbles = bubbles.filter((bubble) => {
    const matchesSearch =
      bubble.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bubble.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || bubble.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div
      className={cn("flex flex-col h-full", className)}
      style={{ backgroundColor: themeProfile.background }}
    >
      {/* Header */}
      <div
        className="h-14 flex items-center justify-between px-4"
        style={{ borderBottom: `1px solid ${themeProfile.border}` }}
      >
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab("bubbles")}
            className={cn(
              "font-literary font-semibold transition-colors",
              activeTab === "bubbles"
                ? "opacity-100"
                : "opacity-50 hover:opacity-100",
            )}
            style={{ color: themeProfile.text }}
          >
            Bubbles
          </button>
          <button
            onClick={() => setActiveTab("notes")}
            className={cn(
              "font-literary font-semibold transition-colors",
              activeTab === "notes"
                ? "opacity-100"
                : "opacity-50 hover:opacity-100",
            )}
            style={{ color: themeProfile.text }}
          >
            Stickies
          </button>
        </div>

        {activeTab === "bubbles" && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="p-2 rounded-lg transition-all hover:scale-105"
            style={{
              backgroundColor: themeProfile.accent,
              color: themeProfile.background,
            }}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {activeTab === "bubbles" ? (
        <>
          {/* Search & Filter */}
          <div className="p-4 space-y-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "var(--text-muted)" }}
              />
              <input
                type="text"
                placeholder="Search bubbles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="cozy-input w-full"
                style={{ paddingLeft: "2.5rem" }}
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterType("all")}
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-medium transition-all",
                )}
                style={{
                  backgroundColor:
                    filterType === "all" ? `${themeProfile.accent}22` : "transparent",
                  color:
                    filterType === "all"
                      ? themeProfile.accent
                      : themeProfile.text,
                  border:
                    filterType === "all"
                      ? `1px solid ${themeProfile.accent}55`
                      : `1px solid transparent`,
                }}
              >
                All
              </button>
              {bubbleTypes.map(({ type, label }) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium transition-all",
                  )}
                  style={{
                    backgroundColor:
                      filterType === type ? `${themeProfile.accent}22` : "transparent",
                    color:
                      filterType === type
                        ? themeProfile.accent
                        : themeProfile.text,
                    border:
                      filterType === type
                        ? `1px solid ${themeProfile.accent}55`
                        : `1px solid transparent`,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Bubble List */}
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
            <div
              className="text-xs mb-2 px-1"
              style={{ color: themeProfile.text, opacity: 0.5 }}
            >
              Drag bubbles to editor to insert text
            </div>
            {filteredBubbles.map((bubble) => {
              const typeInfo = bubbleTypes.find((t) => t.type === bubble.type);
              const Icon = typeInfo?.icon || StickyNote;
              const colorClass =
                typeInfo?.color || "text-slate-500 bg-slate-50";

              return (
                <div
                  key={bubble.id}
                  draggable
                  onDragStart={() => setDraggedBubble(bubble.id)}
                  onDragEnd={() => setDraggedBubble(null)}
                  onClick={() => setSelectedBubble(bubble.id)}
                  className={cn(
                    "group p-3 rounded-xl cursor-move transition-all duration-200",
                    "hover:shadow-md hover:-translate-y-0.5",
                  )}
                  style={{
                    backgroundColor: themeProfile.paperSurface,
                    border: `1px solid ${themeProfile.border}`,
                  }}
                >
                  <div className="flex items-start gap-2">
                    {/* Drag Handle */}
                    <div
                      className="flex-shrink-0 pt-1 opacity-40 group-hover:opacity-100 transition-opacity"
                      style={{ color: themeProfile.text }}
                    >
                      <GripVertical className="w-4 h-4" />
                    </div>

                    <div
                      className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                        colorClass,
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4
                          className="font-medium text-sm truncate"
                          style={{ color: themeProfile.text }}
                        >
                          {bubble.name}
                        </h4>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBubble(bubble.id);
                          }}
                          className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ color: themeProfile.text, opacity: 0.5 }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#ef4444";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = themeProfile.text;
                            e.currentTarget.style.opacity = "0.5";
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {bubble.content && (
                        <p
                          className="text-xs line-clamp-2 mt-1"
                          style={{ color: themeProfile.text, opacity: 0.6 }}
                        >
                          {bubble.content}
                        </p>
                      )}

                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className={cn(
                            "text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider",
                            colorClass,
                          )}
                        >
                          {bubble.type}
                        </span>
                        {bubble.connections.length > 0 && (
                          <span
                            className="text-[10px] flex items-center gap-0.5"
                            style={{ color: themeProfile.text, opacity: 0.5 }}
                          >
                            <Link2 className="w-3 h-3" />
                            {bubble.connections.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredBubbles.length === 0 && (
              <div
                className="flex flex-col items-center justify-center py-12"
                style={{ color: themeProfile.text, opacity: 0.6 }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: themeProfile.paperSurface }}
                >
                  <Search className="w-5 h-5" />
                </div>
                <p className="text-sm">No bubbles found</p>
                <p className="text-xs opacity-60">Create one to get started</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 px-4 py-4 overflow-auto custom-scrollbar">
          <div className="space-y-4">
            {filteredStickies.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-12"
                style={{ color: themeProfile.text, opacity: 0.6 }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: themeProfile.paperSurface }}
                >
                  <StickyNote className="w-5 h-5" />
                </div>
                <p className="text-sm" style={{ color: themeProfile.text }}>
                  No stickies found
                </p>
                <p
                  className="text-xs"
                  style={{ color: themeProfile.text, opacity: 0.6 }}
                >
                  Create stickies on the Sticky Board
                </p>
              </div>
            ) : (
              filteredStickies.map((note) => {
                const isSelected = selectedNoteId === note.id;

                let linkedNoteIds: string[] = [];
                if (selectedNoteId) {
                  linkedNoteIds = [
                    ...new Set(
                      stickyLinks
                        .filter(
                          (l) =>
                            l.fromId === selectedNoteId ||
                            l.toId === selectedNoteId,
                        )
                        .map((l) =>
                          l.fromId === selectedNoteId ? l.toId : l.fromId,
                        ),
                    ),
                  ];
                }

                const isLinked = linkedNoteIds.includes(note.id);
                const isHighlighted = isSelected || isLinked;

                const selectedNoteColor = selectedNoteId
                  ? stickyNotes.find((n) => n.id === selectedNoteId)?.color
                  : null;
                const bgColor = isHighlighted
                  ? isSelected
                    ? note.color
                    : selectedNoteColor || note.color
                  : note.color;

                return (
                  <div
                    key={note.id}
                    onClick={() =>
                      setSelectedNoteId(isSelected ? null : note.id)
                    }
                    className={cn(
                      "p-4 rounded-xl cursor-pointer transition-all duration-200 border",
                      isHighlighted
                        ? "scale-[1.02] shadow-md border-black/20"
                        : "hover:scale-[1.01] hover:shadow border-black/5 opacity-80",
                      isSelected ? "ring-2 ring-offset-2 ring-black/10" : "",
                    )}
                    style={{
                      backgroundColor: bgColor,
                      color: "#1c1917",
                    }}
                  >
                    <div className="font-medium whitespace-pre-wrap text-sm leading-relaxed font-literary">
                      {note.content || (
                        <span className="opacity-50 italic">Empty note</span>
                      )}
                    </div>
                    {isLinked && (
                      <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase font-bold opacity-60 bg-black/5 w-fit px-2 py-1 rounded-full">
                        <Link2 className="w-3 h-3" />
                        Linked attached sticky
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {showCreateForm && (
        <div
          className="absolute inset-0 z-50 p-4 animate-gentle-fade-in"
          style={{
            backgroundColor: themeProfile.background,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className="font-literary font-semibold"
              style={{ color: themeProfile.text }}
            >
              Create Bubble
            </h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="p-2 rounded-lg transition-colors"
              style={{ color: themeProfile.text, opacity: 0.6 }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeProfile.paperSurface;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wider mb-2"
                style={{ color: themeProfile.text, opacity: 0.6 }}
              >
                Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {bubbleTypes.map(({ type, label, icon: Icon, color }) => (
                  <button
                    key={type}
                    onClick={() => setNewBubbleType(type)}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150",
                    )}
                    style={{
                      backgroundColor:
                        newBubbleType === type
                          ? themeProfile.paperSurface
                          : "transparent",
                      borderColor:
                        newBubbleType === type
                          ? themeProfile.accent
                          : "transparent",
                    }}
                  >
                    <Icon className={cn("w-5 h-5", color.split(" ")[0])} />
                    <span
                      className="text-xs"
                      style={{ color: themeProfile.text }}
                    >
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wider mb-2"
                style={{ color: themeProfile.text, opacity: 0.6 }}
              >
                Name
              </label>
              <input
                type="text"
                value={newBubbleName}
                onChange={(e) => setNewBubbleName(e.target.value)}
                placeholder="e.g., Marcus, The Old Castle..."
                className="cozy-input w-full"
                autoFocus
              />
            </div>

            <div>
              <label
                className="block text-xs font-medium uppercase tracking-wider mb-2"
                style={{ color: themeProfile.text, opacity: 0.6 }}
              >
                Notes <span className="normal-case opacity-60">(optional)</span>
              </label>
              <textarea
                value={newBubbleContent}
                onChange={(e) => setNewBubbleContent(e.target.value)}
                placeholder="Add details..."
                className="cozy-input w-full h-24 resize-none"
              />
            </div>

            <button
              onClick={handleCreateBubble}
              disabled={!newBubbleName.trim()}
              className="w-full py-2.5 rounded-xl font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
            >
              Create Bubble
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
